import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CrudTable, type CrudColumn } from '@/shared/components/ui/crud-table';
import { useIsAdminTier } from '@/shared/auth/SessionProvider';
import { StatusBadge } from '@/shared/components/ui/status-badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import {
  listArticulosByCotizable,
  parseArticuloNumber,
  softDeleteArticulo,
  type ArticuloItem,
  type CotizableFlag,
} from '@/shared/api/controllers/articulos';
import {
  findActiveInventarioByArticulo,
  softDeleteInventario,
  calcStockDisponible,
  type InventarioItem,
} from '@/shared/api/controllers/inventario';
import { parseMoney } from '@/shared/lib/money';
import { useActiveCountry } from '@/shared/countries/ActiveCountryProvider';
import { invalidateAbmRelatedQueries } from '@/shared/api/abmCacheInvalidation';
import type { EntityViewProps } from '../../types';
import { ArticuloDialog } from './ArticuloDialog';
import { ServicioDialog } from './ServicioDialog';

type Mode =
  | { kind: 'closed' }
  | { kind: 'create' }
  | { kind: 'edit'; item: ArticuloItem }
  | { kind: 'delete'; item: ArticuloItem };

// Locale follows the currency so each currency gets its native symbol:
// es-MX renders MXN as `$`, es-AR renders ARS as `$` and USD as `US$`.
// Without this, mixing locales leaves MXN as the literal `MXN 191` while
// ARS/USD already have a $ glyph.
const money = (n: number | null | undefined, currency: 'ARS' | 'MXN' | 'USD') => {
  if (n == null) return '—';
  const locale = currency === 'MXN' ? 'es-MX' : 'es-AR';
  // Show cents when present (e.g. costo_contable cargado del Excel
  // viene con 2 decimales), pero omitir `.00` para integers limpios.
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(n);
};

export function ArticulosEntity({
  search,
  filterValue,
  onStatus,
  openCreate,
  onCreateHandled,
  cotizable,
  entitySingular,
}: EntityViewProps & { cotizable: CotizableFlag; entitySingular: string }) {
  const queryClient = useQueryClient();
  const { config: activeCountry } = useActiveCountry();
  const isMx = activeCountry.code === 'MX';
  // Country code appended so AR/MX caches stay isolated even when the
  // cotizable filter matches.
  const queryKey = useMemo(
    () => ['configuracion', 'articulos', cotizable, activeCountry.code] as const,
    [cotizable, activeCountry.code],
  );

  const { data: rows = [], isPending, isFetching, error, refetch } = useQuery({
    queryKey,
    queryFn: () => listArticulosByCotizable(cotizable),
    staleTime: 60_000,
  });
  const [mode, setMode] = useState<Mode>({ kind: 'closed' });

  useEffect(() => {
    if (openCreate) {
      setMode({ kind: 'create' });
      onCreateHandled();
    }
  }, [openCreate, onCreateHandled]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let sorted = [...rows].sort((a, b) =>
      (a.fields.Articulo_ART ?? '').localeCompare(b.fields.Articulo_ART ?? '', 'es'),
    );
    if (filterValue) {
      sorted = sorted.filter((r) => r.fields.Status_ART === filterValue);
    }
    if (!q) return sorted;
    return sorted.filter((r) => {
      const a = (r.fields.Articulo_ART ?? '').toLowerCase();
      const c = (r.fields.Codigo_ART ?? '').toLowerCase();
      const cc = (r.fields.CuentaContable_ART ?? '').toLowerCase();
      return a.includes(q) || c.includes(q) || cc.includes(q);
    });
  }, [rows, search, filterValue]);

  // Column sets diverge by cotizable because the dialogs diverge: Productos
  // (cotizable=SI) collect Código + ARS/USD prices via ArticuloDialog, while
  // Servicios (cotizable=NO) only collect Categoría + Cuenta Contable +
  // Descripción via ServicioDialog. Rendering the Productos column set for
  // Servicios left those three columns permanently blank / "-".
  const columns: CrudColumn<ArticuloItem>[] =
    cotizable === 'SI'
      ? [
          {
            header: 'Status',
            width: 'w-32',
            render: (r) => <StatusBadge status={r.fields.Status_ART} />,
          },
          {
            header: 'Código',
            width: 'w-28',
            render: (r) => (
              <span className="text-foreground">{r.fields.Codigo_ART || '—'}</span>
            ),
          },
          {
            header: entitySingular,
            render: (r) => (
              <span className="font-medium text-foreground">
                {r.fields.Articulo_ART || '—'}
              </span>
            ),
          },
          {
            // The local-currency column reads from `valor_unitario`,
            // which stores ARS in AR rows and MXN in MX rows. Header
            // and formatting follow the active country.
            header: isMx ? 'Valor MXN' : 'Valor ARS',
            render: (r) => (
              <span>
                {money(parseArticuloNumber(r.fields.ValorUn_ART), isMx ? 'MXN' : 'ARS')}
              </span>
            ),
          },
          {
            header: 'Valor USD',
            render: (r) => (
              <span>{money(parseArticuloNumber(r.fields.ValorUnDolar_ART), 'USD')}</span>
            ),
          },
          // MX-only column. Costo Contable applies to any article now
          // (it feeds the Nova economics for whatever carries a cost);
          // show the value when set, '—' otherwise.
          ...(isMx
            ? [
                {
                  header: 'Costo Contable',
                  render: (r: ArticuloItem) => {
                    const v = parseArticuloNumber(r.fields.CostoContable_ART);
                    return v != null ? (
                      <span>{money(v, 'MXN')}</span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    );
                  },
                },
              ]
            : []),
          {
            header: 'Cuenta Contable',
            render: (r) => (
              <span className="text-muted-foreground">
                {r.fields.CuentaContable_ART || '—'}
              </span>
            ),
          },
        ]
      : [
          {
            header: 'Status',
            width: 'w-32',
            render: (r) => <StatusBadge status={r.fields.Status_ART} />,
          },
          {
            header: 'Categoría',
            render: (r) => (
              <span className="text-foreground">{r.fields.Categoria_ART || '—'}</span>
            ),
          },
          {
            header: 'Cuenta Contable',
            render: (r) => (
              <span className="text-muted-foreground">
                {r.fields.CuentaContable_ART || '—'}
              </span>
            ),
          },
          {
            header: 'Descripción',
            render: (r) => (
              <span className="font-medium text-foreground">
                {r.fields.Articulo_ART || '—'}
              </span>
            ),
          },
        ];

  const closeDialog = () => setMode({ kind: 'closed' });

  const canEdit = useIsAdminTier();
  return (
    <>
      <CrudTable
        hideActions={!canEdit}
        rows={filtered}
        columns={columns}
        loading={isPending}
        error={error instanceof Error ? error : null}
        onRetry={() => refetch()}
        emptyMessage={search ? `No hay resultados para "${search}".` : 'Sin registros.'}
        onEdit={(r) => setMode({ kind: 'edit', item: r })}
        onDelete={(r) => setMode({ kind: 'delete', item: r })}
        canEdit={(r) => r.fields.Status_ART !== 'Inactivo'}
        editDisabledTitle={() => 'El registro está inactivo'}
        canDelete={(r) => r.fields.Status_ART !== 'Inactivo'}
        deleteDisabledTitle={() => 'El registro ya está inactivo'}
        isBackgroundFetching={isFetching}
      />

      {(mode.kind === 'create' || mode.kind === 'edit') &&
        (cotizable === 'NO' ? (
          <ServicioDialog
            open
            onOpenChange={(o) => !o && closeDialog()}
            item={mode.kind === 'edit' ? mode.item : null}
            queryKey={queryKey}
            onSuccess={(msg) => {
              closeDialog();
              onStatus({ kind: 'success', message: msg });
            }}
            onError={(msg) => {
              closeDialog();
              onStatus({ kind: 'error', message: msg });
            }}
          />
        ) : (
          <ArticuloDialog
            open
            onOpenChange={(o) => !o && closeDialog()}
            item={mode.kind === 'edit' ? mode.item : null}
            existingRows={rows}
            cotizable={cotizable}
            queryKey={queryKey}
            entitySingular={entitySingular}
            onSuccess={(msg) => {
              closeDialog();
              onStatus({ kind: 'success', message: msg });
            }}
            onError={(msg) => {
              closeDialog();
              onStatus({ kind: 'error', message: msg });
            }}
          />
        ))}

      {mode.kind === 'delete' && (
        <DeleteArticuloDialog
          open
          onOpenChange={(o) => !o && closeDialog()}
          item={mode.item}
          entitySingular={entitySingular}
          onSuccess={(msg) => {
            queryClient.invalidateQueries({ queryKey });
            invalidateAbmRelatedQueries(queryClient, 'articulos');
            closeDialog();
            onStatus({ kind: 'success', message: msg });
          }}
          onError={(msg) => {
            closeDialog();
            onStatus({ kind: 'error', message: msg });
          }}
        />
      )}
    </>
  );
}

function DeleteArticuloDialog({
  open,
  onOpenChange,
  item,
  entitySingular,
  onSuccess,
  onError,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  item: ArticuloItem;
  entitySingular: string;
  onSuccess: (m: string) => void;
  onError: (m: string) => void;
}) {
  // Lookup the matching inventory row. If it exists and is Activo we
  // surface a warning + cascade the soft-delete on confirm. Failures
  // here don't block the delete — worst case we fall back to the
  // historical behavior (article-only soft-delete) and the user can
  // retire any straggler inventory row manually.
  const inventarioQ = useQuery({
    queryKey: ['articulo-inventario', item.id],
    queryFn: () => findActiveInventarioByArticulo(item.id),
    enabled: open,
    staleTime: 30_000,
  });

  const inventario: InventarioItem | null = inventarioQ.data ?? null;
  const totalStock = inventario
    ? parseMoney(inventario.fields.TotalStock_I) ?? 0
    : 0;
  const stockDisponible = calcStockDisponible(inventario);
  const hasStock = totalStock > 0 || stockDisponible > 0;

  const mutation = useMutation({
    mutationFn: async () => {
      await softDeleteArticulo(item.id);
      // Cascade: if there's still an active inventory row, retire it
      // too so the Inventario module doesn't keep showing a row whose
      // article is gone (the inconsistency that triggered this guard).
      if (inventario) {
        try {
          await softDeleteInventario(inventario.id);
        } catch (err) {
          // Article delete already succeeded — log and move on. Worst
          // case the user can retry; the article won't reappear.
          // eslint-disable-next-line no-console
          console.warn(
            '[articulos] cascade inventory soft-delete failed',
            inventario.id,
            err,
          );
        }
      }
    },
    onSuccess: () => onSuccess(`${entitySingular} eliminado correctamente.`),
    onError: (err) =>
      onError(err instanceof Error ? err.message : `No se pudo eliminar el ${entitySingular.toLowerCase()}.`),
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !mutation.isPending && onOpenChange(o)}>
      <DialogContent
        className="sm:max-w-md"
        processing={mutation.isPending && 'Eliminando…'}
      >
        <DialogHeader>
          <DialogTitle>Eliminar {entitySingular.toLowerCase()}</DialogTitle>
          <DialogDescription>
            ¿Confirmás la eliminación de{' '}
            <strong className="text-foreground">
              {item.fields.Articulo_ART || item.fields.Codigo_ART || '—'}
            </strong>
            ?
          </DialogDescription>
        </DialogHeader>
        {inventarioQ.isLoading ? (
          <div className="px-6 pb-4">
            <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
              Verificando inventario…
            </div>
          </div>
        ) : hasStock ? (
          <div className="px-6 pb-4">
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              <p className="font-medium">Atención: este artículo tiene inventario activo.</p>
              <p className="mt-1 text-xs leading-relaxed">
                Stock total: <strong>{totalStock}</strong>
                {stockDisponible !== totalStock && (
                  <>
                    {' '}· Disponible: <strong>{stockDisponible}</strong>
                  </>
                )}
                . Si continuás, también se eliminará la fila de inventario
                correspondiente.
              </p>
            </div>
          </div>
        ) : null}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button
            variant="destructive"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending || inventarioQ.isLoading}
          >
            {hasStock ? 'Eliminar de todas formas' : 'Eliminar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
